import {
  type Country,
  type Locale,
  type SalesforceLanguageCodeEnum,
} from '@/types/regionTypes';

import {
  taxMessagesLabel,
  vatMessages,
  vatMessagesDetail,
} from '@/components/SalesTaxBadge/messages';
import type {
  CountryCode,
  LanguageCodeEnum,
  MoneyFragment,
} from '@/graphql/saleor/api';
import {
  CONSUMPTION_TAX_INCLUDED_COUNTRIES,
  GST_INCLUDED_COUNTRIES,
  NEXT_PUBLIC_DEFAULT_APP_LOCALE,
  NO_TAX_COUNTRIES,
  TAX_EXCLUDED_COUNTRIES,
  VAT_INCLUDED_COUNTRIES,
} from '@/lib/const';

import { LocaleConfiguration } from './configuration/configuration';
import type { Maybe } from './types';

/**
 * @deprecated
 * Breitling to CLDR language code mapping
 * `slug`: Breitling's code
 * `code`: CLDR code
 */
export const LANGUAGES: Record<string, Locale> = {
  enUS: {
    locale: 'en-US',
    code: 'EN_US',
    name: 'English',
    slug: 'en',
    sfCode: 'EN',
  },
  enGB: {
    locale: 'en-GB',
    code: 'EN_GB',
    name: 'English',
    slug: 'en',
    sfCode: 'EN',
  },
  es: { locale: 'es', code: 'ES', name: 'Español', slug: 'es', sfCode: 'ES' },
  de: { locale: 'de', code: 'DE', name: 'Deutsch', slug: 'de', sfCode: 'DE' },
  fr: { locale: 'fr', code: 'FR', name: 'Français', slug: 'fr', sfCode: 'FR' },
  it: { locale: 'it', code: 'IT', name: 'Italiano', slug: 'it', sfCode: 'IT' },
  ja: { locale: 'ja', code: 'JA', name: '日本語', slug: 'jp', sfCode: 'JA' },
  ko: { locale: 'ko', code: 'KO', name: '한국어', slug: 'ko', sfCode: 'KO' },
  pt: {
    locale: 'pt',
    code: 'PT',
    name: 'Português',
    slug: 'pt',
    sfCode: 'PT',
  },
  ru: { locale: 'ru', code: 'RU', name: 'Русский', slug: 'ru', sfCode: 'RU' },
  zh: {
    locale: 'zh',
    code: 'ZH_HANS',
    name: '中文(简体)',
    slug: 'zh',
    sfCode: 'ZH_CN',
  },
  zht: {
    locale: 'zh-Hant',
    code: 'ZH_HANT',
    name: '中文（繁體）',
    slug: 'zht',
    sfCode: 'ZH', // Missing corresponding language code in Salesforce
  },
};

/**
 * @deprecated
 * Country and locale mapping of Saleor channels.
 */
export const SUPPORTED_COUNTRIES: Country[] = [
  {
    channelSlug: 'us-usd',
    code: 'US',
    currency: 'USD',
    defaultLocale: 'en-US',
    flag: 'us',
    locales: [LANGUAGES.enUS, LANGUAGES.es, LANGUAGES.zh],
    name: 'United States',
    slug: 'us',
  },
  {
    channelSlug: 'ca-cad',
    code: 'CA',
    currency: 'CAD',
    defaultLocale: 'en-CA',
    flag: 'ca',
    locales: [LANGUAGES.enUS, LANGUAGES.fr, LANGUAGES.zh],
    name: 'Canada',
    slug: 'ca',
  },
] as Country[];

export const DEFAULT_COUNTRY = SUPPORTED_COUNTRIES.find((country) =>
  country.locales.find(
    (locale) =>
      `${country.slug}-${locale.slug}` === NEXT_PUBLIC_DEFAULT_APP_LOCALE,
  ),
)!;
if (!DEFAULT_COUNTRY) {
  throw Error(
    `Cannot map default country for ${NEXT_PUBLIC_DEFAULT_APP_LOCALE}`,
  );
}

export const DEFAULT_LANGUAGE = LANGUAGES.enUS;

/**
 * @param routerLocale - i.e. `pl-pl`
 */
export const getRegionFromLocale = (
  routerLocale = NEXT_PUBLIC_DEFAULT_APP_LOCALE,
) => {
  const supportedCountries =
    LocaleConfiguration.getInstance().getSupportedCountries();
  const country =
    supportedCountries.find(({ locales, slug: countrySlug }) =>
      locales.find(
        ({ slug: localeSlug }) =>
          `${countrySlug}-${localeSlug}` === routerLocale,
      ),
    ) || DEFAULT_COUNTRY;

  const locale =
    country.locales.find(({ slug }) => slug === routerLocale.split('-')[1]) ||
    country.locales[0];

  return { country, locale };
};

/**
 * @param routerLocale - i.e. `pl-pl`
 */
export const getGqlRegionFromLocale = (
  routerLocale = NEXT_PUBLIC_DEFAULT_APP_LOCALE,
) => {
  const { country, locale } = getRegionFromLocale(routerLocale);
  return { channel: country.channelSlug, languageCode: locale.code };
};

export const getLocaleForCountry = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  countryCode: CountryCode | string | undefined,
) => {
  const supportedCountries =
    LocaleConfiguration.getInstance().getSupportedCountries();
  const country =
    supportedCountries.find((country) => country.code === countryCode) ||
    DEFAULT_COUNTRY;

  return `${country.slug}-${country.locales[0].slug}`;
};

export const getLocaleForCountryAndLanguage = (
  countryCode: CountryCode | string | undefined,
  languageCode: SalesforceLanguageCodeEnum | LanguageCodeEnum | undefined,
  preventDefault = true,
) => {
  const supportedCountries =
    LocaleConfiguration.getInstance().getSupportedCountries();
  let country = supportedCountries.find(
    (country) => country.code === countryCode,
  );
  if (!preventDefault && !country) {
    country = DEFAULT_COUNTRY;
  }
  let language = country?.locales?.find(
    (language) => language.sfCode === languageCode,
  );
  if (!preventDefault && !language) {
    language = country?.locales[0];
  }

  return country && language ? `${country.slug}-${language.slug}` : 'zz-zz';
};

export const getCountryForChannel = (channelSlug: string) =>
  LocaleConfiguration.getInstance()
    .getSupportedCountries()
    .find((country) => country.channelSlug === channelSlug) || DEFAULT_COUNTRY;

export const getCountryByCountryCode = (
  countryCode?: string,
): Country | undefined => {
  if (!countryCode) return undefined;

  return LocaleConfiguration.getInstance()
    .getSupportedCountries()
    .find((country) => country.code === countryCode);
};

export const getContentfulRegionFromLocale = (
  routerLocale = NEXT_PUBLIC_DEFAULT_APP_LOCALE,
) => {
  const { country, locale } = getRegionFromLocale(routerLocale);
  return { countryCode: country.slug, languageCode: locale.locale };
};

export const getCountryCodeByChannel = (channel?: string) =>
  channel?.split('-')[0].toUpperCase();

export const checkIfVatIncluded = (channel?: string) => {
  const countryCode = getCountryCodeByChannel(channel);
  return [
    ...VAT_INCLUDED_COUNTRIES,
    ...GST_INCLUDED_COUNTRIES,
    ...CONSUMPTION_TAX_INCLUDED_COUNTRIES,
  ]?.includes(countryCode as CountryCode);
};

export const checkIfAllTaxesExcluded = (channel?: string) => {
  const countryCode = getCountryCodeByChannel(channel);
  return NO_TAX_COUNTRIES?.includes(countryCode as CountryCode);
};

type Message = {
  defaultMessage: string;
  id: string;
};

type GeneralMessages = {
  [key: string]: Message;
};

const getTaxMessage = (
  countryCode: CountryCode,
  messages: GeneralMessages,
  taxeAmount?: number,
) => {
  if (CONSUMPTION_TAX_INCLUDED_COUNTRIES?.includes(countryCode))
    return messages.consumptionIncluded;
  if (VAT_INCLUDED_COUNTRIES?.includes(countryCode))
    return messages.vatIncluded;
  if (GST_INCLUDED_COUNTRIES?.includes(countryCode)) return messages.inclGST;
  if (taxeAmount && taxeAmount > 0) return messages.inclSalesTax;
  if (TAX_EXCLUDED_COUNTRIES?.includes(countryCode))
    return messages.exclSalesTax;
  return null;
};

export const getTaxMessageShort = (channel?: string, taxeAmount?: number) => {
  const countryCode = channel?.split('-')[0].toUpperCase() as CountryCode;
  return getTaxMessage(countryCode, vatMessages, taxeAmount);
};

export const getTaxMessageDetail = (channel?: string, taxeAmount?: number) => {
  const countryCode = channel?.split('-')[0].toUpperCase() as CountryCode;
  return getTaxMessage(countryCode, vatMessagesDetail, taxeAmount);
};

/**
 * Return the translated tax label for tax amount line of shopping bag or order.
 * @param channel
 */
export const getOrderTaxAmountLabel = (channel?: string) => {
  const countryCode = channel?.split('-')[0].toUpperCase() as CountryCode;
  if (CONSUMPTION_TAX_INCLUDED_COUNTRIES?.includes(countryCode))
    return taxMessagesLabel.consumption;
  if (VAT_INCLUDED_COUNTRIES?.includes(countryCode))
    return taxMessagesLabel.vat;
  else if (GST_INCLUDED_COUNTRIES?.includes(countryCode))
    return taxMessagesLabel.gst;
  else if (TAX_EXCLUDED_COUNTRIES?.includes(countryCode))
    return taxMessagesLabel.salesTax;
  return null;
};

export const isValidCountryCode = (countryCode: string) => {
  const supportedCountries =
    LocaleConfiguration.getInstance().getSupportedCountries();
  return (
    supportedCountries.findIndex((country) => country.code === countryCode) !==
    -1
  );
};

export const getTotalTaxMessage = (
  netPrice: Maybe<Omit<MoneyFragment, '__typename'>>,
  price: Maybe<Omit<MoneyFragment, '__typename'>>,
  channel?: string,
  isCheckoutSelectedAddress?: boolean,
) => {
  if (!netPrice || !price || !netPrice.amount || !price.amount) {
    return '';
  }

  const countryCode = channel?.split('-')[0].toUpperCase() as CountryCode;
  if (CONSUMPTION_TAX_INCLUDED_COUNTRIES?.includes(countryCode)) {
    return vatMessages.consumptionIncluded;
  } else if (VAT_INCLUDED_COUNTRIES?.includes(countryCode)) {
    return vatMessages.vatIncluded;
  } else if (netPrice.amount < price.amount) {
    return GST_INCLUDED_COUNTRIES?.includes(countryCode)
      ? vatMessages.inclGST
      : vatMessages.inclSalesTax;
  } else if (
    TAX_EXCLUDED_COUNTRIES?.includes(countryCode) &&
    isCheckoutSelectedAddress
  ) {
    return vatMessages.inclSalesTax;
  }

  return vatMessages.exclSalesTax;
};
